<template>
  <div class="property-search">
    <div class="search-header text-center mb-4">
      <h4 class="text-primary">Find Your Property</h4>
      <p class="text-muted">Enter your LR Number to locate your property in our records</p>
    </div>

    <form @submit.prevent="searchProperty" class="search-form">
      <div class="form-group">
        <label for="lrNumber" class="form-label">
          <i class="fas fa-search"></i> LR Number *
        </label>
        <input
            type="text"
            class="form-control form-control-lg"
            id="lrNumber"
            v-model="searchForm.lrNumber"
            placeholder="e.g., KERICHO/KAPSOROK/1909 or 631/1513"
            required
            :disabled="loading"
        >
        <small class="form-text text-muted">
          Enter the complete LR Number as it appears on your title deed
        </small>
      </div>

      <div class="form-group">
        <label for="ownerName" class="form-label">
          <i class="fas fa-user"></i> Owner Name (Optional)
        </label>
        <input
            type="text"
            class="form-control"
            id="ownerName"
            v-model="searchForm.ownerName"
            placeholder="Enter owner name for additional verification"
            :disabled="loading"
        >
        <small class="form-text text-muted">
          This helps us verify you're searching for the correct property
        </small>
      </div>

      <div class="search-actions">
        <button
            type="submit"
            class="btn btn-primary btn-lg btn-block"
            :disabled="loading || !searchForm.lrNumber.trim()"
        >
          <span v-if="loading" class="spinner-border spinner-border-sm mr-2"></span>
          <i v-else class="fas fa-search mr-2"></i>
          {{ loading ? 'Searching...' : 'Search Property' }}
        </button>
      </div>
    </form>

    <!-- Search Results -->
    <div v-if="searchResults.length > 0" class="search-results mt-4">
      <h5 class="mb-3">Search Results:</h5>
      <div class="property-card" v-for="property in searchResults" :key="property.id">
        <div class="card">
          <div class="card-body">
            <div class="row">
              <div class="col-md-8">
                <h6 class="card-title text-primary">{{ property.LRNumber }}</h6>
                <p class="card-text">
                  <strong>Owner:</strong> {{ property.ownerNames }}<br>
                  <strong>Location:</strong> {{ property.percelAddress }}<br>
                  <strong>Sub-County:</strong> {{ property.subCountyName }}<br>
                  <strong>Ward:</strong> {{ property.wardName }}<br>
                  <strong>Size:</strong> {{ property.percelSize }}
                </p>
              </div>
              <div class="col-md-4 text-right">
                <button
                    class="btn btn-success"
                    @click="selectProperty(property)"
                >
                  <i class="fas fa-check mr-1"></i>
                  Select This Property
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Results Message -->
    <div v-if="showNoResults" class="no-results mt-4">
      <div class="alert alert-warning">
        <h5><i class="fas fa-exclamation-triangle mr-2"></i>Property Not Found</h5>
        <p class="mb-2">We couldn't find a property with the LR Number: <strong>{{ searchForm.lrNumber }}</strong></p>
        <p class="mb-0">Please check the following:</p>
        <ul class="mt-2 mb-0">
          <li>Ensure the LR Number is spelled correctly</li>
          <li>Include all forward slashes (/) as they appear on your title deed</li>
          <li>Contact our offices if you believe this is an error</li>
        </ul>
      </div>
    </div>

    <!-- Help Section -->
    <div class="help-section mt-4">
      <div class="card bg-light">
        <div class="card-body">
          <h6 class="card-title">
            <i class="fas fa-question-circle text-info mr-2"></i>
            Need Help Finding Your LR Number?
          </h6>
          <p class="card-text small">
            Your LR Number can be found on:
          </p>
          <ul class="small mb-2">
            <li>Title Deed document</li>
            <li>Previous land rates payment receipts</li>
            <li>Property transfer documents</li>
            <li>Survey documents</li>
          </ul>
          <p class="small mb-0">
            <strong>Contact Information:</strong><br>
            Phone: 0700-000-000 | Email: landrates@county.go.ke
          </p>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
/* eslint-disable */
import {execute, authUrl, biller, landrate} from '@/api'

export default {
  name: 'PropertySearch',
  props: {
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchForm: {
        lrNumber: '',
        ownerName: ''
      },
      searchResults: [],
      showNoResults: false
    }
  },
  methods: {
    async searchProperty() {
      if (!this.searchForm.lrNumber.trim()) {
        this.$emit('search-error', 'Please enter an LR Number')
        return
      }

      try {
        this.$emit('loading', true, 'Searching for property...')

        const formData = new FormData()
        formData.append('function', 'searchPropertyForUpdate')
        formData.append('lrNumber', this.searchForm.lrNumber.trim())
        if (this.searchForm.ownerName.trim()) {
          formData.append('ownerName', this.searchForm.ownerName.trim())
        }

        const response = await execute(formData, landrate)

        if (response.data.success) {
          this.searchResults = Array.isArray(response.data.data.properties)
              ? response.data.data.properties
              : [response.data.data.property]
          this.showNoResults = false

          // If only one result, auto-select it
          if (this.searchResults.length === 1) {
            setTimeout(() => {
              this.selectProperty(this.searchResults[0])
            }, 1000)
          }
        } else {
          this.searchResults = []
          this.showNoResults = true
        }
      } catch (error) {
        this.$emit('search-error', 'Error searching for property. Please try again.')
        console.error('Search error:', error)
      } finally {
        this.$emit('loading', false)
      }
    },

    selectProperty(property) {
      this.$emit('property-found', property)
    },

    formatLRNumber(value) {
      // Auto-format LR number as user types
      return value.toUpperCase()
    }
  },

  watch: {
    'searchForm.lrNumber'(newVal) {
      this.searchForm.lrNumber = this.formatLRNumber(newVal)
      this.showNoResults = false
    }
  }
}
</script>

<style scoped>
.property-search {
  max-width: 600px;
  margin: 0 auto;
}

.search-header h4 {
  color: #2c3e50;
  font-weight: 600;
}

.form-label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
}

.form-label i {
  color: #007bff;
  margin-right: 5px;
}

.form-control {
  border-radius: 8px;
  border: 2px solid #e9ecef;
  padding: 12px 15px;
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

.form-control-lg {
  font-size: 1.1rem;
  font-weight: 500;
}

.search-actions {
  margin-top: 25px;
}

.btn-primary {
  background: linear-gradient(45deg, #007bff, #0056b3);
  border: none;
  border-radius: 8px;
  padding: 12px 20px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0,123,255,0.4);
}

.search-results {
  animation: fadeInUp 0.5s ease;
}

.property-card {
  margin-bottom: 15px;
}

.property-card .card {
  border: 2px solid #e9ecef;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.property-card .card:hover {
  border-color: #007bff;
  box-shadow: 0 5px 15px rgba(0,123,255,0.1);
}

.card-title {
  font-weight: 600;
  font-size: 1.1rem;
}

.btn-success {
  border-radius: 6px;
  font-weight: 500;
}

.no-results {
  animation: fadeIn 0.5s ease;
}

.help-section {
  margin-top: 30px;
}

.help-section .card {
  border: none;
  border-radius: 10px;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* Responsive Design */
@media (max-width: 768px) {
  .property-card .col-md-4 {
    text-align: left !important;
    margin-top: 15px;
  }

  .property-card .btn-success {
    width: 100%;
  }
}
</style>